import { DirectionalIcon } from '@batiflow/ui';
import { Body1, Button } from '@fluentui/react-components';
import { DoorArrowLeft24Regular, ErrorCircle24Regular } from '@fluentui/react-icons';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { useRuntime } from '@/app/providers/runtime-provider';
import { platformLoginUrl } from '@/app/runtime';
import { FullPageState } from '@/shared/components/full-page-state';

export const SIGNED_OUT_REASONS = [
  'login_failed',
  'cancelled',
  'not_platform_admin',
  'webauthn_required',
  'step_up_failed',
] as const;

/** Why a sign-in or a step-up did not complete (set by the BFF, PlatformLoginService). */
export const SignedOutSearchSchema = z.object({
  reason: z.enum(SIGNED_OUT_REASONS).optional().catch(undefined),
});
export type SignedOutReason = (typeof SIGNED_OUT_REASONS)[number];

/**
 * Public page (no session needed): after a sign-out or when a sign-in or step-up did not
 * complete. Says what happened in plain words and offers to sign in again.
 */
export function SignedOutPage({ reason }: { reason?: SignedOutReason | undefined }) {
  const { t } = useTranslation('backoffice');
  const { replayPendingAction } = useRuntime();
  // Whatever brought the admin here, an action that was waiting for a step-up must not run later.
  useEffect(() => {
    void replayPendingAction(true);
  }, [replayPendingAction]);

  const key = reason ? `signedOut.reasons.${reason}` : 'signedOut';
  return (
    <FullPageState
      // The door's arrow points out of the page: mirrored in Arabic.
      icon={reason ? <ErrorCircle24Regular /> : <DirectionalIcon icon={DoorArrowLeft24Regular} />}
      role={reason ? 'alert' : 'status'}
      title={t(`${key}.title`)}
      actions={
        <Button appearance="primary" as="a" href={platformLoginUrl('/')}>
          {t('signedOut.signIn')}
        </Button>
      }
    >
      <Body1 as="p">{t(`${key}.body`)}</Body1>
    </FullPageState>
  );
}
